// src/pages/UpperPrimary.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import LearningItemCard from "../components/LearningItemCard";
import { FaArrowLeft } from "react-icons/fa";

/**
 * Upper-Primary (Grades 5-8)
 */
export default function UpperPrimary() {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("All");
  const [favorites, setFavorites] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("fav_upper") || "[]");
    } catch {
      return [];
    }
  });

  useEffect(() => {
    localStorage.setItem("fav_upper", JSON.stringify(favorites));
  }, [favorites]);

  const items = [
{
  id: "u-english",
  title: "English — Tenses",
  description: "Learn English tenses in a fun cartoon-style way.",
  text: "📘 Welcome to Tense World!\n\nImagine three cartoon friends: Present Panda 🐼, Past Parrot 🦜, and Future Fox 🦊.\n\n1️⃣ Present Tense (What is happening now)\nPresent Panda says: 'I play football.'\nThis tense talks about daily habits or things happening now.\nExamples: I eat rice. She reads a book.\n\n2️⃣ Past Tense (What already happened)\nPast Parrot says: 'I played football yesterday.'\nThis tense talks about completed actions.\nExamples: I visited the zoo. They watched a movie.\n\n3️⃣ Future Tense (What will happen)\nFuture Fox says: 'I will play football tomorrow.'\nThis tense talks about future plans.\nExamples: I will travel. She will study.\n\n🎯 Activity: Write 3 sentences in each tense and draw a small cartoon to represent them!",
  video: "https://youtu.be/97ZgXLQxkpU?si=pGhBvmXrMLjz9ify",
  audio: null,
},
{
  id: "u-math",
  title: "Math — Fractions & Decimals",
  description: "Learn fractions and decimals using a pizza cartoon story.",
  text: "🍕 Welcome to Pizza Math!\n\nImagine a cartoon chef cutting a pizza into slices.\nA fraction shows parts of a whole pizza.\n\nIf a pizza has 4 slices and you eat 1 slice → 1/4.\nTo convert into decimal, divide top number by bottom number.\nExample:\n1/2 = 1 ÷ 2 = 0.5\n3/4 = 3 ÷ 4 = 0.75\n2/5 = 2 ÷ 5 = 0.4\n\nDecimals are just another way to write fractions.\n\n🎯 Practice Mission:\nConvert 5/10, 7/8, and 9/4 into decimals.\nDraw your own cartoon pizza and color the fraction you eat!",
  video: "https://youtu.be/mDh1M4bEIbA?si=fXHIu1MzAMu_jgvO",
  audio: null,
},
{
  id: "u-science",
  title: "Science — Motion & Cells",
  description: "Learn motion and cells with fun cartoon examples.",
  text: "🚗 Motion Adventure & 🔬 Cell City!\n\n🏃 Motion:\nImagine a cartoon boy running from home to school.\nWhen his position changes, that is called Motion.\nIf a car moves from point A to point B, it shows motion.\nEnergy helps objects move.\n\n🔬 Cells:\nNow zoom into Cell City!\nCells are tiny building blocks of life.\nPlant cells have a cell wall and chloroplast.\nAnimal cells do not have a cell wall.\nAll living things are made of cells.\n\n🎯 Activity:\nDraw a moving object and label it.\nDraw a simple plant cell and animal cell cartoon.",
  video: "https://youtu.be/qHkUOlC8Nbo?si=RUzkbF0EsnqInWTq",
  audio: null,
},

  ];

  const filteredItems = useMemo(() => {
    const q = (search || "").trim().toLowerCase();
    const rf = (filter || "All").toLowerCase();

    return items.filter((it) => {
      const title = (it.title || "").toLowerCase();
      const description = (it.description || "").toLowerCase();
      const text = (it.text || "").toLowerCase();

      const hasText = Boolean(it.text && String(it.text).trim().length > 0);
      const hasVideo = Boolean(it.video && String(it.video).trim().length > 0);
      const hasAudio = Boolean(it.audio && String(it.audio).trim().length > 0);

      const matchesSearch = q.length === 0 || title.includes(q) || description.includes(q) || text.includes(q);

      let matchesFilter = true;
      if (rf === "text") matchesFilter = hasText;
      else if (rf === "video") matchesFilter = hasVideo;
      else if (rf === "audio") matchesFilter = hasAudio;

      return matchesSearch && matchesFilter;
    });
  }, [items, search, filter]);

  function toggleFav(id) {
    setFavorites((prev) => (prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]));
  }

  return (
    <div className="min-h-screen p-6 bg-gradient-to-br from-blue-50 to-blue-100">
      <div className="max-w-5xl mx-auto">
        {/* Nav */}
        <div className="flex items-center justify-between mb-6">
          <button onClick={() => navigate("/elearning")} className="inline-flex items-center gap-2 bg-white px-3 py-2 rounded shadow">
            <FaArrowLeft /> Back
          </button>
          <h1 className="text-3xl font-bold">Upper-Primary</h1>
          <div />
        </div>

        {/* Search + Filter */}
        <div className="flex flex-col md:flex-row gap-3 mb-6">
          <input
            type="text"
            placeholder="Search lessons (title, description, text)..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="flex-1 px-4 py-2 rounded-lg border bg-white shadow"
          />

          <select value={filter} onChange={(e) => setFilter(e.target.value)} className="px-4 py-2 rounded-lg border bg-white shadow md:w-48">
            <option value="All">All</option>
            <option value="Text">Text</option>
            <option value="Video">Video</option>
            <option value="Audio">Audio</option>
          </select>
        </div>

        {/* Items */}
        <div className="grid gap-6">
          {filteredItems.map((it) => (
            <LearningItemCard key={it.id} item={it} isFav={favorites.includes(it.id)} toggleFav={toggleFav} />
          ))}
        </div>
      </div>
    </div>
  );
}




